import { HelisaStageQuery } from './helisa-stage-query';
import { QueryStage } from './query-stages';

export enum QueryStageType {
  MATCH = 1,
  GROUP = 2,
  PROJECT = 3,
  UNWIND = 4,
  SORT = 5,
  LOOKUP = 6,
  REPLACE_ROOT = 7,
  PAGINATION = 8,
  HAVING = 9,
  UNION = 10,
}

export type FilterOperationValue =
  | HelisaQueryFilter
  | string
  | number
  | Date
  | HelisaField
  | HelisaQueryHaving
  | boolean
  | HelisaQueryCondition
  | HelisaOperation
  | HelisaQueryFunction
  | HelisaQuerySubquery
  | string[];

export type AggregationFunctionType = 'avg' | 'count' | 'max' | 'min' | 'sum';

export type DateTimeFunctionType =
  | 'age'
  | 'clock_timestamp()'
  | 'current_date'
  | 'current_time'
  | 'current_timestamp'
  | 'date_part'
  | 'date_trunc'
  | 'extract'
  | 'justify_days'
  | 'justify_hours'
  | 'justify_interval'
  | 'make_date'
  | 'make_time'
  | 'make_timestamp'
  | 'make_timestamptz'
  | 'now()'
  | 'statement_timestamp()'
  | 'timeofday()'
  | 'transaction_timestamp()';

export type FormatingFunctionType =
  | 'to_char'
  | 'to_date'
  | 'to_json'
  | 'to_jsonb'
  | 'to_number'
  | 'to_timestamp';

export type JsonFunctionType =
  | 'json_array_length'
  | 'json_build_array'
  | 'json_build_object'
  | 'json_object'
  | 'json_object_keys'
  | 'json_strip_nulls'
  | 'json_typeof'
  | 'jsonb_array_length'
  | 'jsonb_build_array'
  | 'jsonb_build_object'
  | 'jsonb_object'
  | 'jsonb_object_keys'
  | 'jsonb_strip_nulls'
  | 'jsonb_typeof'
  | 'row_to_json';

export type NullFunctionType = 'coalesce' | 'num_nonnulls' | 'num_nulls';

export type NumberFunctionType =
  | 'abs'
  | 'cbrt'
  | 'ceil'
  | 'ceiling'
  | 'degrees'
  | 'div'
  | 'exp'
  | 'factorial'
  | 'floor'
  | 'ln'
  | 'log'
  | 'mod'
  | 'pi()'
  | 'power'
  | 'round'
  | 'radians'
  | 'random()'
  | 'scale'
  | 'sign'
  | 'sqrt'
  | 'trunc'
  | 'width_bucket';

export type StringFunctionType =
  | 'ascii'
  | 'character_length'
  | 'char_length'
  | 'chr'
  | 'concat'
  | 'concat_ws'
  | 'initcap'
  | 'left'
  | 'lower'
  | 'lpad'
  | 'ltrim'
  | 'md5'
  | 'overlay'
  | 'parse_ident'
  | 'position'
  | 'repeat'
  | 'replace'
  | 'reverse'
  | 'right'
  | 'rpad'
  | 'rtrim'
  | 'strpos'
  | 'substring'
  | 'translate'
  | 'trim'
  | 'upper';

export type SubqueryFunctionType = 'all' | 'any' | 'exists' | 'some';

export type WindowsFunctionType =
  | 'cume_dist'
  | 'dense_rank'
  | 'first_value'
  | 'last_value'
  | 'nth_value'
  | 'percent_rank'
  | 'rank'
  | 'row_number';

export class HelisaQueryFilter {
  leftValue: FilterOperationValue;
  rightValue: FilterOperationValue | null;
  operation:
    | '$eq'
    | '$lt'
    | '$gt'
    | '$lte'
    | '$gte'
    | '$ne'
    | '$and'
    | '$or'
    | '$regex'
    | '$not'
    | '$start'
    | '$contain'
    | '$isNull'
    | '$notIsNull'
    | '$in';

  constructor(
    leftValue: FilterOperationValue,
    rightValue: FilterOperationValue | null,
    operation:
      | '$eq'
      | '$lt'
      | '$gt'
      | '$lte'
      | '$gte'
      | '$regex'
      | '$and'
      | '$or'
      | '$not'
      | '$ne'
      | '$start'
      | '$contain'
      | '$isNull'
      | '$notIsNull'
      | '$in'
  ) {
    this.leftValue = leftValue;
    this.rightValue = rightValue;
    this.operation = operation;
  }

  // tslint:disable-next-line:no-any
  generateFilter(): any {
    // tslint:disable-next-line:no-any
    const leftValue: any = this.getValue(this.leftValue);
    // tslint:disable-next-line:no-any
    let rightValue: any = null;
    if (this.rightValue !== null) {
      rightValue = this.getValue(this.rightValue);
    }
    if ((leftValue as HelisaQueryHaving).type) {
      const leftHaving: HelisaQueryHaving = leftValue as HelisaQueryHaving;
      // tslint:disable-next-line:no-any
      const regex: any =
        rightValue && (rightValue as HelisaQueryHaving).fieldName
          ? (rightValue as HelisaQueryHaving).fieldName
          : rightValue;
      const regexType: string | null =
        rightValue && (rightValue as HelisaQueryHaving).type
          ? (rightValue as HelisaQueryHaving).type
          : null;
      if (this.operation === '$not') {
        return {
          $not: {
            input: leftHaving.fieldName,
            type: leftHaving.type,
          },
        };
      } else if (this.operation === '$regex') {
        return {
          $regexMatch: {
            input: leftHaving.fieldName,
            type: leftHaving.type,
            regex: '.*' + regex + '.*',
            regexType,
            delimiter: (leftValue as HelisaQueryHaving).delimiter,
            options: 'i',
          },
        };
      } else if (this.operation === '$start') {
        return {
          $regexMatch: {
            input: leftHaving.fieldName,
            type: leftHaving.type,
            regex: '^' + regex + '.*',
            regexType,
            delimiter: leftHaving.delimiter,
            options: 'i',
          },
        };
      } else if (this.operation === '$contain') {
        return {
          $eq: {
            input: leftHaving.fieldName,
            type: leftHaving.type,
            regex: [regex],
            regexType,
            delimiter: leftHaving.delimiter,
          },
        };
      } else if (this.operation === '$notIsNull') {
        return {
          $ifNull: {
            input: leftHaving.fieldName,
            type: leftHaving.type,
            regex: false,
            regexType,
            delimiter: leftHaving.delimiter,
          },
        };
      } else if (this.operation === '$isNull') {
        return {
          $not: {
            $ifNull: {
              input: leftHaving.fieldName,
              type: leftHaving.type,
              regex: false,
              regexType,
              delimiter: leftHaving.delimiter,
            },
          },
        };
      } else {
        // tslint:disable-next-line:no-any
        const obj: any = {};
        obj[this.operation] = {
          input: leftHaving.fieldName,
          type: leftHaving.type,
          regex,
          regexType,
          delimiter: leftHaving.delimiter,
        };
        return obj;
      }
    } else {
      if (this.operation === '$not') {
        return { $not: leftValue };
      } else {
        if (this.operation === '$regex') {
          return {
            $regexMatch: {
              input: leftValue,
              regex: '.*' + rightValue + '.*',
              options: 'i',
            },
          };
        } else if (this.operation === '$start') {
          return {
            $regexMatch: {
              input: leftValue,
              regex: '^' + rightValue + '.*',
              options: 'i',
            },
          };
        } else if (this.operation === '$contain') {
          // tslint:disable-next-line:no-any
          const obj: any = {};
          // tslint:disable-next-line:no-string-literal
          obj['$eq'] = [leftValue, [rightValue]];
          return obj;
        } else if (this.operation === '$in') {
          // tslint:disable-next-line:no-any
          const obj: any = {};
          // tslint:disable-next-line:no-string-literal
          obj['$in'] = [
            leftValue,
            Array.isArray(rightValue) ? [...rightValue] : rightValue,
          ];
          return obj;
        } else if (this.operation === '$notIsNull') {
          // tslint:disable-next-line:no-any
          const obj: any = {};
          // tslint:disable-next-line:no-string-literal
          obj['$ifNull'] = [leftValue, false];
          return obj;
        } else if (this.operation === '$isNull') {
          // tslint:disable-next-line:no-any
          const obj: any = {};
          // tslint:disable-next-line:no-string-literal
          obj['$not'] = [{ $ifNull: [leftValue, false] }];
          return obj;
        } else {
          // tslint:disable-next-line:no-any
          const obj: any = {};
          obj[this.operation] = [leftValue, rightValue];
          return obj;
        }
      }
    }
  }

  // tslint:disable-next-line:no-any
  private getValue(value: FilterOperationValue): any {
    // tslint:disable-next-line:no-any
    let responseValue: any = null;
    if (value instanceof HelisaQueryFilter) {
      responseValue = value.generateFilter();
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      responseValue = value;
    } else if (Array.isArray(value)) {
      responseValue = value;
    } else if (value instanceof Date) {
      responseValue = { $toDate: value };
    } else if (value instanceof HelisaQueryCondition) {
      responseValue = generateCondition(value);
    } else if (value instanceof HelisaQueryFunction) {
      responseValue = generateFunction(value);
    } else if (value instanceof HelisaQuerySubquery) {
      responseValue = generateSubquery(value);
    } else if (value instanceof HelisaOperation) {
      responseValue = value.generateOperation();
    } else if (
      !!(value as HelisaQueryHaving) &&
      (value as HelisaQueryHaving).type
    ) {
      responseValue = generateHaving(value as HelisaQueryHaving);
    } else {
      responseValue = '$' + value?.fieldName;
    }
    return responseValue;
  }
}

export interface HelisaQuerySort {
  fieldName: string;
  order: 'asc' | 'desc';
}

export interface HelisaQueryGroup {
  fieldName: string | undefined | null;
  fieldResponseName: string;
}

export interface HelisaQueryAggregation {
  fieldResponseName: string;
  fieldName: string | undefined | null;
  helisaQueryCondition: HelisaQueryCondition | undefined | null;
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg'
    | undefined
    | null;
  operation: HelisaOperation | undefined | null;
  sort?: Array<HelisaQuerySort> | undefined | null;
  delimiter?: string | undefined | null;
  functionField?: HelisaQueryFunction | undefined | null;
}

export class HelisaOperation {
  type: 'multiply' | 'divide' | 'add' | 'subtract' | 'sum' | 'max' | 'max_uuid';
  operatorOne: number | Date | HelisaField | HelisaOperation;
  operatorTwo: number | Date | HelisaField | HelisaOperation | null;

  constructor(
    type:
      | 'multiply'
      | 'divide'
      | 'add'
      | 'subtract'
      | 'sum'
      | 'max'
      | 'max_uuid',
    operatorOne: number | Date | HelisaField | HelisaOperation,
    operatorTwo: number | Date | HelisaField | HelisaOperation | null
  ) {
    this.type = type;
    this.operatorOne = operatorOne;
    this.operatorTwo = operatorTwo;
  }

  // tslint:disable-next-line:no-any
  public generateOperation(): any {
    // tslint:disable-next-line:no-any
    const operator: any = {};
    // tslint:disable-next-line:no-any
    const operatorOne: any = this.getValue(this.operatorOne);
    if (
      this.type === 'sum' ||
      this.type === 'max' ||
      this.type === 'max_uuid'
    ) {
      operator['$' + this.type] = [operatorOne];
    } else {
      // tslint:disable-next-line:no-any
      const operatorTwo: any = this.getValue(this.operatorTwo as any);
      // tslint:disable-next-line:no-any
      operator['$' + this.type] = [operatorOne, operatorTwo];
    }
    return operator;
  }

  // tslint:disable-next-line:no-any
  private getValue(value: number | Date | HelisaField | HelisaOperation): any {
    // tslint:disable-next-line:no-any
    let responseValue: any = null;
    if (value instanceof HelisaOperation) {
      responseValue = value.generateOperation();
    } else if (typeof value === 'number') {
      responseValue = value;
    } else if (value instanceof Date) {
      responseValue = { $toDate: value };
    } else {
      responseValue = '$' + value.fieldName;
    }
    return responseValue;
  }
}

export interface HelisaField {
  fieldName: string;
}

export interface HelisaGroupField {
  ids: Array<HelisaQueryGroup>;
  aggregates: Array<HelisaQueryAggregation>;
}

export interface HelisaConditionalField {
  fieldName: string;
  condition: HelisaQueryCondition;
}

export interface HelisaUnwindField {
  fieldName: string;
  preserveNullAndEmptyArrays: boolean;
}

export interface HelisaProjectField {
  fieldName: string;
  fieldFullName: string;
  hideField?: boolean;
  fixedValue?: string;
}

export interface HelisaOperationField {
  fieldName: string;
  operation: HelisaOperation;
}

export class HelisaQueryCondition {
  filter: HelisaQueryFilter;
  trueValue: string | number | HelisaField | HelisaQueryFilter;
  falseValue: string | number | HelisaField | HelisaQueryFilter;

  constructor(
    filter: HelisaQueryFilter,
    trueValue: string | number | HelisaField | HelisaQueryFilter,
    falseValue: string | number | HelisaField | HelisaQueryFilter
  ) {
    this.filter = filter;
    this.trueValue = trueValue;
    this.falseValue = falseValue;
  }
}

export class HelisaQueryFunction {
  functionType:
    | AggregationFunctionType
    | DateTimeFunctionType
    | FormatingFunctionType
    | JsonFunctionType
    | NullFunctionType
    | NumberFunctionType
    | StringFunctionType
    | SubqueryFunctionType
    | WindowsFunctionType;
  fields: FilterOperationValue[];

  constructor(
    functionType:
      | AggregationFunctionType
      | DateTimeFunctionType
      | FormatingFunctionType
      | JsonFunctionType
      | NullFunctionType
      | NumberFunctionType
      | StringFunctionType
      | SubqueryFunctionType
      | WindowsFunctionType,
    fields: FilterOperationValue[]
  ) {
    this.functionType = functionType;
    this.fields = fields;
  }
}

export class HelisaQuerySubquery {
  from: string;
  subqueryStages: QueryStage[];

  constructor(from: string, subqueryStages: QueryStage[]) {
    this.from = from;
    this.subqueryStages = subqueryStages;
  }
}

export class HelisaQueryHaving {
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg';
  fieldName: string;
  delimiter: string;

  constructor(
    type:
      | AggregationFunctionType
      | 'first'
      | 'array_agg'
      | 'json_agg'
      | 'json_object_agg'
      | 'string_agg',
    fieldName: string,
    delimiter?: string
  ) {
    this.type = type;
    this.fieldName = fieldName;
    this.delimiter = delimiter || '';
  }
}

export interface HelisaFunctionField {
  fieldName: string;
  functionObject: HelisaQueryFunction;
}

export interface HelisaSubqueryField {
  fieldName: string;
  subqueryObject: HelisaQuerySubquery;
}

export interface LookupLetField {
  fieldAlias: string;
  fieldName: string;
}

export interface HelisaLookup {
  sourceView: string;
  letFields: Array<LookupLetField>;
  pipeline: HelisaStageQuery;
  lookupAlias: string;
  joinType?: '$lj' | '$rj' | '$ij' | '$fj' | '$cj' | '';
}

export interface HelisaReplaceRootField {
  arrayElement: string;
  newRoot: string;
}

export interface HelisaFilter {
  sourceField: string;
  sourceFieldAlias: string;
  condition: HelisaQueryFilter;
}

export interface HelisaFilterField {
  fieldName: string;
  filter: HelisaFilter;
}

// tslint:disable-next-line:no-any
export function generatePagination(page: number, size: number): Array<any> {
  return [{ $skip: page * size }, { $limit: size }];
}

// tslint:disable-next-line:no-any
export function generateUnion(
  unionOperator: '$us' | '$ua' | '$ui' | '$ue' | undefined | null
): any {
  return { $union: unionOperator };
}

// tslint:disable-next-line:no-any
export function generateCondition(
  helisaQueryCondition: HelisaQueryCondition
): any {
  // tslint:disable-next-line:no-any
  const obj: any = {
    if: helisaQueryCondition.filter.generateFilter(),
    then:
      helisaQueryCondition.trueValue instanceof HelisaQueryFilter
        ? helisaQueryCondition.trueValue.generateFilter()
        : helisaQueryCondition.trueValue,
    else:
      helisaQueryCondition.falseValue instanceof HelisaQueryFilter
        ? helisaQueryCondition.falseValue.generateFilter()
        : helisaQueryCondition.falseValue,
  };
  return { $cond: obj };
}

// tslint:disable-next-line:no-any
export function generateFilter(helisaFilter: HelisaFilter): any {
  // tslint:disable-next-line:no-any
  const obj: any = {
    input: '$' + helisaFilter.sourceField,
    as: helisaFilter.sourceFieldAlias,
    cond: helisaFilter.condition.generateFilter(),
  };
  return { $filter: obj };
}

// tslint:disable-next-line:no-any
export function generateFunction(helisaFunction: HelisaQueryFunction): any {
  // tslint:disable-next-line:no-any
  const obj: any = {
    functionType: helisaFunction.functionType,
    // tslint:disable-next-line:typedef
    fields: helisaFunction.fields.map((item: FilterOperationValue) => {
      if (item instanceof HelisaQueryFilter) {
        return item.generateFilter();
      } else if (
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean'
      ) {
        return item;
      } else if (Array.isArray(item)) {
        return item;
      } else if (item instanceof Date) {
        return { $toDate: item };
      } else if (item instanceof HelisaQueryCondition) {
        return generateCondition(item);
      } else if (item instanceof HelisaQueryFunction) {
        return generateFunction(item);
      } else if (item instanceof HelisaQuerySubquery) {
        return generateSubquery(item);
      } else if (item instanceof HelisaOperation) {
        return item.generateOperation();
      } else if ((item as HelisaQueryHaving).type) {
        return generateHaving(item as HelisaQueryHaving);
      } else {
        return '$' + item?.fieldName;
      }
    }),
  };
  return { $functionField: obj };
}

// tslint:disable-next-line:no-any
export function generateSubquery(helisaSubquery: HelisaQuerySubquery): any {
  // tslint:disable-next-line:no-any
  const subquery: any[] = [];
  // tslint:disable-next-line:typedef
  helisaSubquery.subqueryStages.forEach((item: QueryStage) => {
    if (item.stageType === QueryStageType.PAGINATION) {
      subquery.push(...item.generateQuery());
    } else {
      subquery.push(item.generateQuery());
    }
  });
  // tslint:disable-next-line:no-any
  const obj: any = {
    from: helisaSubquery.from,
    subquery,
  };
  return { $subqueryField: obj };
}

// tslint:disable-next-line:no-any
export function generateHaving(helisaHaving: HelisaQueryHaving): any {
  return {
    type: '$' + helisaHaving.type,
    fieldName: '$' + helisaHaving.fieldName,
    delimiter: helisaHaving.delimiter || '',
  };
}

// tslint:disable-next-line:no-any
export function generateSorts(helisaQuerySort: HelisaQuerySort[]): any {
  // tslint:disable-next-line:no-any
  const sort: any = {};
  helisaQuerySort.forEach((fieldSort: HelisaQuerySort): void => {
    sort[fieldSort.fieldName] = fieldSort.order === 'asc' ? 1 : -1;
  });
  return { $sort: sort };
}

// tslint:disable-next-line:no-shadowed-variable
export function isHelisaProjectField(
  field:
    | HelisaProjectField
    | HelisaConditionalField
    | HelisaOperationField
    | HelisaFilterField
    | HelisaFunctionField
    | HelisaSubqueryField
): field is HelisaProjectField {
  return (field as HelisaProjectField).fieldFullName !== undefined;
}

// tslint:disable-next-line:no-shadowed-variable
export function isHelisaConditionalField(
  field:
    | HelisaProjectField
    | HelisaConditionalField
    | HelisaOperationField
    | HelisaFilterField
    | HelisaFunctionField
    | HelisaSubqueryField
): field is HelisaConditionalField {
  return (field as HelisaConditionalField).condition !== undefined;
}

// tslint:disable-next-line:no-shadowed-variable
export function isHelisaFunctionField(
  field:
    | HelisaProjectField
    | HelisaConditionalField
    | HelisaOperationField
    | HelisaFilterField
    | HelisaFunctionField
    | HelisaSubqueryField
): field is HelisaFunctionField {
  return (field as HelisaFunctionField).functionObject !== undefined;
}

// tslint:disable-next-line:no-shadowed-variable
export function isHelisaSubqueryField(
  field:
    | HelisaProjectField
    | HelisaConditionalField
    | HelisaOperationField
    | HelisaFilterField
    | HelisaFunctionField
    | HelisaSubqueryField
): field is HelisaSubqueryField {
  return (field as HelisaSubqueryField).subqueryObject !== undefined;
}

// tslint:disable-next-line:no-shadowed-variable
export function isHelisaFilterField(
  field:
    | HelisaProjectField
    | HelisaConditionalField
    | HelisaOperationField
    | HelisaFilterField
    | HelisaFunctionField
    | HelisaSubqueryField
): field is HelisaFilterField {
  return (field as HelisaFilterField).filter !== undefined;
}

export function projectField(
  fieldName: string,
  fieldFullName: string,
  hideField: boolean = false,
  fixedValue?: string
): HelisaProjectField {
  return { fieldName, fieldFullName, hideField, fixedValue };
}

export function operationField(
  fieldName: string,
  operation: HelisaOperation
): HelisaOperationField {
  return { fieldName, operation };
}

export function functionField(
  fieldName: string,
  functionObject: HelisaQueryFunction
): HelisaFunctionField {
  return { fieldName, functionObject };
}

export function subqueryField(
  fieldName: string,
  subqueryObject: HelisaQuerySubquery
): HelisaSubqueryField {
  return { fieldName, subqueryObject };
}

export function sortField(
  fieldName: string,
  order: 'asc' | 'desc'
): HelisaQuerySort {
  return { fieldName, order };
}

export function unwindField(
  fieldName: string,
  preserveNullAndEmptyArrays: boolean
): HelisaUnwindField {
  return { fieldName, preserveNullAndEmptyArrays };
}

export function addGroupId(
  fieldName: string,
  fieldResponseName: string
): HelisaQueryGroup {
  return { fieldResponseName, fieldName };
}

export function addOperation(
  type: 'multiply' | 'divide' | 'add' | 'subtract' | 'sum' | 'max' | 'max_uuid',
  operatorOne: number | Date | HelisaField | HelisaOperation,
  operatorTwo: number | Date | HelisaField | HelisaOperation | null
): HelisaOperation {
  return new HelisaOperation(type, operatorOne, operatorTwo);
}

export function addSumOperation(
  type: 'sum',
  operatorOne: number | Date | HelisaField | HelisaOperation
): HelisaOperation {
  return new HelisaOperation(type, operatorOne, null);
}

export function addGroupField(
  ids: Array<HelisaQueryGroup>,
  aggregates: Array<HelisaQueryAggregation>
): HelisaGroupField {
  return { ids, aggregates };
}

export function addHelisaLookup(
  sourceView: string,
  letFields: Array<LookupLetField>,
  pipeline: HelisaStageQuery,
  lookupAlias: string,
  joinType?: '$lj' | '$rj' | '$ij' | '$fj' | '$cj' | ''
): HelisaLookup {
  return { sourceView, letFields, pipeline, lookupAlias, joinType };
}

export function addReplaceRootField(
  arrayElement: string,
  newRoot: string
): HelisaReplaceRootField {
  return { arrayElement, newRoot };
}

export function addLookupLetField(
  fieldAlias: string,
  fieldName: string
): LookupLetField {
  return { fieldAlias, fieldName };
}

// tslint:disable-next-line:no-shadowed-variable
export function addHelisaFilter(
  sourceField: string,
  sourceFieldAlias: string,
  condition: HelisaQueryFilter
): HelisaFilter {
  return { sourceField, sourceFieldAlias, condition };
}

export function addHelisaFilterField(
  fieldName: string,
  filter: HelisaFilter
): HelisaFilterField {
  return { fieldName, filter };
}

export function addHelisaQueryFunction(
  functionType:
    | AggregationFunctionType
    | DateTimeFunctionType
    | FormatingFunctionType
    | JsonFunctionType
    | NullFunctionType
    | NumberFunctionType
    | StringFunctionType
    | SubqueryFunctionType
    | WindowsFunctionType,
  fields: FilterOperationValue[]
): HelisaQueryFunction {
  return new HelisaQueryFunction(functionType, fields);
}

export function addHelisaQuerySubquery(
  from: string,
  subqueryStages: QueryStage[]
): HelisaQuerySubquery {
  return new HelisaQuerySubquery(from, subqueryStages);
}

export function addHelisaQueryHaving(
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  fieldName: string,
  delimiter?: string
): HelisaQueryHaving {
  return { type, fieldName, delimiter: delimiter || '' };
}

export function field(fieldName: string): HelisaField {
  return { fieldName };
}

export function conditionalField(
  fieldName: string,
  helisaQueryCondition: HelisaQueryCondition
): HelisaConditionalField {
  return { fieldName, condition: helisaQueryCondition };
}

export function condition(
  filter: HelisaQueryFilter,
  trueValue: string | number | HelisaField | HelisaQueryFilter,
  falseValue: string | number | HelisaField | HelisaQueryFilter
): HelisaQueryCondition {
  if (
    typeof trueValue !== 'string' &&
    typeof trueValue !== 'number' &&
    !(trueValue instanceof HelisaQueryFilter)
  ) {
    trueValue = '$' + trueValue.fieldName;
  }
  if (
    typeof falseValue !== 'string' &&
    typeof falseValue !== 'number' &&
    !(falseValue instanceof HelisaQueryFilter)
  ) {
    falseValue = '$' + falseValue.fieldName;
  }
  return new HelisaQueryCondition(filter, trueValue, falseValue);
}

export function addAggregation(
  fieldResponseName: string,
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  fieldName?: string
): HelisaQueryAggregation {
  if (type !== 'count' && !fieldName) {
    throw new Error('Debe enviar el field name para la suma y el promedio');
  }
  return {
    fieldResponseName,
    fieldName,
    helisaQueryCondition: null,
    type,
    operation: null,
    sort: null,
    delimiter: null,
    functionField: null,
  };
}

export function addAggregationConditionalField(
  fieldResponseName: string,
  type:
    | AggregationFunctionType
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  helisaQueryCondition: HelisaQueryCondition
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName: null,
    helisaQueryCondition,
    type,
    operation: null,
    sort: null,
    delimiter: null,
    functionField: null,
  };
}

export function addAggregationWithOperation(
  fieldResponseName: string,
  type:
    | AggregationFunctionType
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  operation: HelisaOperation
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName: null,
    helisaQueryCondition: null,
    type,
    operation,
    sort: null,
    delimiter: null,
    functionField: null,
  };
}

export function addSingleAggregation(
  fieldResponseName: string,
  fieldName: string
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName,
    helisaQueryCondition: null,
    type: null,
    operation: null,
    sort: null,
    delimiter: null,
    functionField: null,
  };
}

// tslint:disable-next-line:no-shadowed-variable
export function addAggregationWithFunctionField(
  fieldResponseName: string,
  fieldName: string,
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  operation: HelisaOperation,
  sort: Array<HelisaQuerySort>,
  delimiter: string,
  functionField: HelisaQueryFunction
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName,
    helisaQueryCondition: null,
    type,
    operation,
    sort,
    delimiter,
    functionField,
  };
}

// tslint:disable-next-line:no-shadowed-variable
export function addAggregationWithFunctionFieldOperation(
  fieldResponseName: string,
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  operation: HelisaOperation,
  sort: Array<HelisaQuerySort>,
  delimiter: string,
  functionField: HelisaQueryFunction
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName: null,
    helisaQueryCondition: null,
    type,
    operation,
    sort,
    delimiter,
    functionField,
  };
}

// tslint:disable-next-line:no-shadowed-variable
export function addAggregationWithFunctionFieldCondition(
  fieldResponseName: string,
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  helisaQueryCondition: HelisaQueryCondition,
  sort: Array<HelisaQuerySort>,
  delimiter: string,
  functionField: HelisaQueryFunction
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName: null,
    helisaQueryCondition,
    type,
    operation: null,
    sort,
    delimiter,
    functionField,
  };
}

// tslint:disable-next-line:no-shadowed-variable
export function addAggregationSingleFunctionField(
  fieldResponseName: string,
  fieldName: string,
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  sort: Array<HelisaQuerySort>,
  delimiter: string,
  functionField: HelisaQueryFunction
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName,
    helisaQueryCondition: null,
    type,
    operation: null,
    sort,
    delimiter,
    functionField,
  };
}

export function addAggregationStringAgg(
  fieldResponseName: string,
  fieldName: string,
  type:
    | AggregationFunctionType
    | 'first'
    | 'array_agg'
    | 'json_agg'
    | 'json_object_agg'
    | 'string_agg',
  sort: Array<HelisaQuerySort>,
  delimiter: string
): HelisaQueryAggregation {
  return {
    fieldResponseName,
    fieldName,
    helisaQueryCondition: null,
    type,
    operation: null,
    sort,
    delimiter,
    functionField: null,
  };
}

export function eq(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$eq');
}

export function inContain(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
    | string[]
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$in');
}

export function contain(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$contain');
}

export function lessThan(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$lt');
}

export function greaterThan(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$gt');
}

export function lessThanEquals(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$lte');
}

export function greaterThanEquals(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$gte');
}

export function like(
  helisaField:
    | HelisaField
    | HelisaQueryHaving
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  text: string
): HelisaQueryFilter {
  return new HelisaQueryFilter(helisaField, text, '$regex');
}

export function startsWith(
  helisaField:
    | HelisaField
    | HelisaQueryHaving
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  text: string
): HelisaQueryFilter {
  return new HelisaQueryFilter(helisaField, text, '$start');
}

export function and(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaQueryCondition
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaQueryCondition
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$and');
}

export function or(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$or');
}

export function not(
  query: HelisaQueryFilter | HelisaQueryFunction | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(query, null, '$not');
}

export function different(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery,
  rightValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, rightValue, '$ne');
}

export function isNull(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, null, '$isNull');
}

export function notIsNull(
  leftValue:
    | HelisaQueryFilter
    | string
    | number
    | Date
    | HelisaField
    | HelisaQueryHaving
    | boolean
    | HelisaOperation
    | HelisaQueryFunction
    | HelisaQuerySubquery
): HelisaQueryFilter {
  return new HelisaQueryFilter(leftValue, null, '$notIsNull');
}
